import { UserAddOutlined } from '@ant-design/icons';
import { t } from '@hefe/i18n';
import { BasketballTeamBasic, Prospect } from '@hefe/models';
import { ModalsSlice, ProspectsApi } from '@hefe/store';
import { formatDate, formatDateTime, fullUserName } from '@hefe/utils';
import { Button, Divider, Popconfirm, Tooltip, Typography } from 'antd';
import { useMemo, useState } from 'react';
import { useAppDispatch } from '../../../../store/store';
import CustomTable from '../../../core/components/table/custom-table.component';
import AddProspectForm from '../../forms/add-prospect/add-prospect.form';
import ProspectComponent from '../prospect/prospect.component';

interface ProspectsComponentProps {
  prospects: Prospect[];
  isFetching: boolean;
  teamId?: number;
}

export default function ProspectsComponent(props: ProspectsComponentProps) {
  const { prospects, isFetching, teamId } = props;
  const dispatch = useAppDispatch();

  const [selectedRowKeys, setSelectedRowKeys] = useState<number[]>([]);

  const [signProspect, { isLoading: isSiginingProspect }] =
    ProspectsApi.useSignProspectMutation();
  const [deleteProspect, { isLoading: isDeletingProspect }] =
    ProspectsApi.useDeleteProspectMutation();

  const selectedRowIsSigned = useMemo(() => {
    if (selectedRowKeys.length !== 1) {
      return true;
    }

    const selectedProspect = prospects.find((p) => p.id === selectedRowKeys[0]);
    if (!selectedProspect) {
      return true;
    }

    return selectedProspect.status === 'signed';
  }, [prospects, selectedRowKeys]);

  return (
    <div>
      <Typography.Text>{t('prospects.info.text')}</Typography.Text>
      <Divider />
      <CustomTable
        name={'prospects'}
        rowKey={'id'}
        loading={isFetching}
        dataSource={prospects}
        onRowSelect={(selectedRowKeys) => setSelectedRowKeys(selectedRowKeys)}
        onRowClick={(prospect: Prospect) => {
          dispatch(
            ModalsSlice.modalsActions.openModal({
              title: fullUserName(prospect),
              width: 800,
              component: (
                <ProspectComponent
                  prospect={prospect}
                  teamId={teamId}
                  key={prospect.id}
                />
              ),
              closable: true,
              hideOkButton: true,
              hideCancelButton: true,
            })
          );
        }}
        columns={[
          {
            key: 'firstname',
            title: t('firstname'),
            dataIndex: 'firstname',
            sortType: 'string',
            showFilter: true,
            default: true,
          },
          {
            key: 'lastname',
            title: t('lastname'),
            dataIndex: 'lastname',
            sortType: 'string',
            showFilter: true,
            default: true,
          },
          {
            key: 'email',
            title: t('email'),
            dataIndex: 'email',
            sortType: 'string',
            showFilter: true,
            default: true,
          },
          {
            key: 'phone',
            title: t('phone'),
            dataIndex: 'phone',
            sortType: 'string',
            showFilter: true,
            default: true,
          },
          {
            key: 'birthday',
            title: t('birthday'),
            dataIndex: 'birthday',
            showDateFilter: true,
            sortType: 'date',
            showFilter: true,
            default: true,
            render: (birthday: string) => {
              return formatDate(birthday);
            },
          },
          {
            key: 'message',
            title: t('prospects.message'),
            dataIndex: 'message',
            sortType: 'string',
            showFilter: true,
            default: true,
            render: (message: string) => {
              if (!message) {
                return '';
              }

              const messageLength = message?.length;
              return (
                message?.slice(0, 25) +
                (messageLength && messageLength > 25 ? '...' : '')
              );
            },
          },
          {
            key: 'experience',
            title: t('prospects.experience.title'),
            dataIndex: 'experience',
            showFilter: true,
            sortType: 'string',
            default: true,
            render: (experience: string) => {
              return t(`prospects.experience.${experience}`);
            },
          },
          {
            key: 'status',
            title: t('prospects.status.title'),
            dataIndex: 'status',
            showFilter: true,
            sortType: 'string',
            default: true,
            render: (status: string) => {
              return t(`prospects.status.${status}`);
            },
          },
          {
            key: 'created_at',
            title: t('created_at'),
            dataIndex: 'created_at',
            showFilter: true,
            showDateFilter: true,
            default: true,
            render: (createdAt: string) => {
              if (!createdAt) {
                return '';
              }
              return formatDateTime(createdAt);
            },
          },
          {
            key: 'teams',
            title: t('teams'),
            dataIndex: 'basketball_teams',
            sortType: 'string',
            showFilter: true,
            default: true,
            render: (teams: BasketballTeamBasic[]) => {
              return teams.map((team) => team.name).join(', ');
            },
          },
        ]}
        addForm={{
          formId: `add-prospect-form`,
          component: <AddProspectForm />,
          title: t('prospects.add'),
        }}
        deleteAction={{
          loading: isDeletingProspect,
          text: t('prospects.info.delete'),
          action: (ids: number[]) => {
            const id = ids[0];
            if (id) {
              const prospect = prospects.find((p) => p.id === id);
              if (prospect) {
                deleteProspect(prospect);
              }
            }
          },
        }}
        toolbarItems={[
          <Tooltip title={t('prospects.sign.title')}>
            <Popconfirm
              title={t('prospects.sign.title')}
              description={t('prospects.sign.description')}
              disabled={selectedRowKeys.length !== 1 || selectedRowIsSigned}
              okButtonProps={{ loading: isSiginingProspect }}
              onConfirm={() => {
                const id = selectedRowKeys[0];
                if (id) {
                  signProspect(id)
                    .unwrap()
                    .then(() => setSelectedRowKeys([]));
                }
              }}
            >
              <Button
                disabled={selectedRowKeys.length !== 1 || selectedRowIsSigned}
                icon={<UserAddOutlined />}
                loading={isSiginingProspect}
              />
            </Popconfirm>
          </Tooltip>,
        ]}
      />
    </div>
  );
}
